import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";

import RegisterModal from "../../components/Tienda/components/LoginNavItem/RegisterModal";
import ForgottenPasswordModal from "../../components/Tienda/components/LoginNavItem/ForgottenPasswordModal";
import { LoginContext } from "../../components/Tienda/components/ParadigmaLoginProvider/ParadigmaLoginProvider";

import "./styles.scss";

const Login = () => {
  const history = useHistory();
  const { logged, login: loginRequest, errorMsg, displayname } =
    useContext(LoginContext);

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const tiendaName = useMemo(
    () => process.env.REACT_APP_TIENDA_NOMBRE || "Bienvenido",
    []
  );

  useEffect(() => {
    const storedUsername = localStorage.getItem("rememberedUsername");
    if (storedUsername) {
      setFormData((prev) => ({ ...prev, username: storedUsername }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (logged) {
      history.replace("/tienda/productos/");
    }
  }, [logged, history]);

  useEffect(() => {
    if (displayname && !formData.username) {
      setFormData((prev) => ({ ...prev, username: displayname }));
    }
  }, [displayname, formData.username]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formData.username.trim() || !formData.password.trim()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem("rememberedUsername", formData.username.trim());
    } else {
      localStorage.removeItem("rememberedUsername");
    }

    setWaitingForResponse(true);
    loginRequest(formData.username.trim(), formData.password.trim(), (ok) => {
      if (!ok) {
        setWaitingForResponse(false);
      }
    });
  };

  return (
    <div className="login-page">
      <div className="login-page__panel">
        <section className="login-page__hero">
          <div className="login-page__hero-inner">
            <img
              className="login-page__logo"
              src={process.env.REACT_APP_TIENDA_LOGO}
              alt={tiendaName}
            />
            <h1 className="login-page__title">¡Bienvenido a {tiendaName}!</h1>
            <p className="login-page__subtitle">
              Consulta todos nuestros productos y gestiona tus compras.
            </p>
          </div>
        </section>

        <section className="login-page__form">
          <div className="login-page__form-card">
            <h2 className="login-page__form-title">Iniciar sesión</h2>
            <p className="login-page__form-subtitle">
              Ingresa tus credenciales para continuar.
            </p>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="login-username">Correo electrónico</Label>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fa fa-user" aria-hidden="true" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    id="login-username"
                    name="username"
                    autoComplete="username"
                    placeholder="ejemplo@correo.com"
                    value={formData.username}
                    disabled={waitingForResponse}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        username: event.target.value,
                      }))
                    }
                  />
                </InputGroup>
              </FormGroup>

              <FormGroup className="mb-3">
                <Label for="login-password">Contraseña</Label>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fa fa-lock" aria-hidden="true" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    id="login-password"
                    name="password"
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Tu contraseña"
                    value={formData.password}
                    disabled={waitingForResponse}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: event.target.value,
                      }))
                    }
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="cursor-pointer"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <i
                        className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                        aria-hidden="true"
                      />
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>

              <Row className="align-items-center mb-4">
                <Col xs="12" md="6">
                  <Label className="login-page__remember">
                    <Input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe((prev) => !prev)}
                    />{" "}
                    Recordarme
                  </Label>
                </Col>
                <Col
                  xs="12"
                  md="6"
                  className="text-md-right login-page__forgot"
                >
                  <button
                    type="button"
                    className="login-page__link"
                    onClick={() => setShowForgotModal(true)}
                    disabled={waitingForResponse}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </Col>
              </Row>

              {errorMsg && (
                <div className="login-page__error" role="alert">
                  {errorMsg}
                </div>
              )}

              <Button
                color="primary"
                block
                size="lg"
                type="submit"
                disabled={
                  waitingForResponse ||
                  !formData.username.trim() ||
                  !formData.password.trim()
                }
              >
                {waitingForResponse ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2" aria-hidden="true" />
                    Validando...
                  </>
                ) : (
                  "Ingresar"
                )}
              </Button>
            </Form>

            <div className="login-page__register">
              <span>¿Aún no tienes cuenta?</span>
              <button
                type="button"
                className="login-page__link"
                onClick={() => setShowRegisterModal(true)}
                disabled={waitingForResponse}
              >
                Crear una cuenta
              </button>
            </div>
          </div>
        </section>
      </div>

      <RegisterModal
        showModal={showRegisterModal}
        toggle={() => setShowRegisterModal((prev) => !prev)}
        openLoginModal={() => setShowRegisterModal(false)}
      />

      <ForgottenPasswordModal
        showModal={showForgotModal}
        toggle={() => setShowForgotModal((prev) => !prev)}
        openLoginModal={() => setShowForgotModal(false)}
      />
    </div>
  );
};

export default Login;
